/**
 * Stable per-install client id, sent as `X-Client-Id` on every API call. The
 * backend stamps it into workspace broadcast payloads (`actor.client_id`) so
 * realtime echo suppression is per DEVICE, not per user — the same account may
 * be signed in on two devices, and each must still receive the other's edits.
 *
 * Per-install (localStorage) rather than per-tab: two windows of one install
 * share optimistic state anyway, while separate devices never do.
 */
import { CLIENT_HEADER_NAME, buildClientHeader } from './clientInfo';

const STORAGE_KEY = 'apibooClientId';

let cached = null;

function newId() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function getClientId() {
  if (cached) return cached;
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = newId();
      localStorage.setItem(STORAGE_KEY, id);
    }
    cached = id;
    return id;
  } catch {
    // localStorage unavailable (private browsing, quota exceeded, disabled).
    // A session-only id still lets this device recognize its OWN broadcasts
    // as self — returning null here would make every one of this device's
    // edits arrive with actor.client_id: null, and fromSelf() treats a
    // missing client id as "not self", so this device's own optimistic
    // edits would be double-applied when its echo comes back.
    cached = newId();
    return cached;
  }
}

/**
 * Attaches the bearer auth token, the X-Client-Id header and the
 * X-Apiboo-Client (kind/version/platform) header to an outgoing axios request
 * config. Shared by the two axios instances (utils/apiClient.js and
 * services/apiService.js) so the header logic can't drift between them. Both
 * instances only ever talk to the Apiboo API; provider and fetch_url traffic
 * goes through utils/tauri.js directly and never passes here.
 */
export function attachAuthHeaders(config) {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const clientId = getClientId();
  if (clientId) {
    config.headers['X-Client-Id'] = clientId;
  }
  config.headers[CLIENT_HEADER_NAME] = buildClientHeader();
  return config;
}
